import { useState, useEffect, type ReactNode } from 'react';
import { useAuth } from '@/context/AuthContext';
import { MetricSegment, KPIStrip } from '@/components/common/MetricCard';
import { ExceptionBand } from '@/components/dashboard/ExceptionBand';
import { PositionBand, type TrendPoint } from '@/components/dashboard/PositionBand';
import { RoleBand } from '@/components/dashboard/RoleBand';
import { dashboardApi, type Exceptions } from '@/api/services/dashboard.api';
import { jobsApi, type Job } from '@/api/services/jobs.api';
import { companiesApi } from '@/api/services/companies.api';
import { PageHeader } from '@/components/common';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { cn, formatCurrency, formatNumber, formatRelativeTime, formatSnakeCase } from '@/lib/utils';
import {
  Package,
  Truck,
  AlertTriangle,
  TrendingUp,
  Wrench,
  Briefcase,
  Undo2,
  ShieldCheck,
  ClipboardCheck,
  Activity,
  Zap,
  BarChart3,
} from 'lucide-react';

// API services
import { analyticsApi } from '@/api/services/analytics.api';
import { inventoryApi } from '@/api/services/inventory.api';
import { workOrdersApi } from '@/api/services/work-orders.api';
import { equipmentApi } from '@/api/services/equipment.api';
import { certificatesApi } from '@/api/services/certificates.api';
import { consumptionApi } from '@/api/services/consumption.api';

// Types
import type { DashboardData, Recommendation } from '@/api/services/analytics.api';
import type { InventoryDashboard } from '@/api/services/inventory.api';
import type { WorkOrderDashboard } from '@/api/services/work-orders.api';
import type { EquipmentDashboard } from '@/api/services/equipment.api';
import type { CertificatesDashboard } from '@/api/services/certificates.api';
import type { ConsumptionDashboard } from '@/api/services/consumption.api';

// ─── Priority Styles ────────────────────────────────────────────────────────────

const priorityStyles: Record<string, string> = {
  CRITICAL: 'bg-danger-100 text-danger-700 border-danger-200',
  HIGH: 'bg-warning-100 text-warning-700 border-warning-200',
  MEDIUM: 'bg-primary-100 text-primary-700 border-primary-200',
  LOW: 'bg-muted text-muted-foreground border-border',
};

// ─── Alert Builder ──────────────────────────────────────────────────────────────

// ─── Recent Activity Builder ────────────────────────────────────────────────────

interface ActivityItem {
  type: 'po' | 'shipment' | 'alert';
  icon: ReactNode;
  typeStyle: string;
  title: string;
  description: string;
  time: string;
  createdAt: string;
}

function buildRecentActivity(analytics: DashboardData | null): ActivityItem[] {
  if (!analytics?.recentActivity) return [];

  const items: ActivityItem[] = [];

  for (const po of analytics.recentActivity.purchaseOrders.slice(0, 5)) {
    items.push({
      type: 'po',
      icon: <Package className="h-4 w-4" />,
      typeStyle: 'bg-status-info-subtle text-status-info-foreground',
      title: `PO ${po.poNumber}`,
      description: `${formatSnakeCase(po.status)}${po.supplier ? ` — ${po.supplier.name}` : ''}${po.totalAmount ? ` (${formatCurrency(po.totalAmount, po.currency)})` : ''}`,
      time: formatRelativeTime(po.createdAt),
      createdAt: po.createdAt,
    });
  }

  for (const shp of analytics.recentActivity.shipments.slice(0, 5)) {
    items.push({
      type: 'shipment',
      icon: <Truck className="h-4 w-4" />,
      typeStyle: 'bg-status-success-subtle text-status-success-foreground',
      title: `Shipment ${shp.shipmentNumber}`,
      description: `${formatSnakeCase(shp.status)}${shp.origin && shp.destination ? ` — ${shp.origin.name} → ${shp.destination.name}` : ''}`,
      time: formatRelativeTime(shp.createdAt),
      createdAt: shp.createdAt,
    });
  }

  for (const alert of analytics.recentActivity.alerts.slice(0, 5)) {
    items.push({
      type: 'alert',
      icon: <AlertTriangle className="h-4 w-4" />,
      typeStyle:
        alert.severity === 'CRITICAL' || alert.severity === 'HIGH'
          ? 'bg-status-destructive-subtle text-status-destructive-foreground'
          : 'bg-status-warning-subtle text-status-warning-foreground',
      title: alert.title,
      description: `${formatSnakeCase(alert.severity)} ${formatSnakeCase(alert.type)}`,
      time: formatRelativeTime(alert.createdAt),
      createdAt: alert.createdAt,
    });
  }

  return items
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 8);
}

// ─── Loading Skeleton ───────────────────────────────────────────────────────────

function DashboardSkeleton() {
  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-5 w-96" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-36 rounded-xl" />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Skeleton className="h-80 rounded-xl" />
        <Skeleton className="h-80 rounded-xl" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Skeleton className="h-64 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
      <Skeleton className="h-48 rounded-xl" />
    </div>
  );
}

// ─── Distribution Bar Helper ────────────────────────────────────────────────────

// ─── Status Row Helper ──────────────────────────────────────────────────────────

// ─── Main Dashboard Component ───────────────────────────────────────────────────

export function Dashboard() {
  const { user, hasPermission } = useAuth();

  // State for all dashboard data
  const [loading, setLoading] = useState(true);
  const [exceptions, setExceptions] = useState<Exceptions | null>(null);
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [companyCount, setCompanyCount] = useState<number | null>(null);
  const [varianceSummary, setVarianceSummary] = useState<{
    averageVariancePercentage: number;
  } | null>(null);
  const [analytics, setAnalytics] = useState<DashboardData | null>(null);
  const [recommendations, setRecommendations] = useState<Recommendation[] | null>(null);
  const [, setInventoryDash] = useState<InventoryDashboard | null>(null);
  const [workOrderDash, setWorkOrderDash] = useState<WorkOrderDashboard | null>(null);
  const [equipmentDash, setEquipmentDash] = useState<EquipmentDashboard | null>(null);
  const [certsDash, setCertsDash] = useState<CertificatesDashboard | null>(null);
  const [, setConsumptionDash] = useState<ConsumptionDashboard | null>(null);

  useEffect(() => {
    async function loadDashboard() {
      setLoading(true);
      const results = await Promise.allSettled([
        analyticsApi.getDashboard(), // [0] Pattern A
        analyticsApi.getRecommendations(), // [1] Pattern A
        inventoryApi.getDashboard(), // [2] Pattern A
        workOrdersApi.getDashboard(), // [3] Pattern B → .data
        equipmentApi.getDashboard(), // [4] Pattern B → .data
        certificatesApi.getDashboard(), // [5] Pattern A
        consumptionApi.getDashboard(), // [6] Pattern B → .data
        dashboardApi.getExceptions(), // [7] Pattern A
        consumptionApi.getTrends({ periods: 14, interval: 'daily' }), // [8] Pattern B → .data
        jobsApi.getAll(), // [9] Pattern A
        consumptionApi.getVarianceAnalysis({
          startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          endDate: new Date().toISOString(),
        }), // [10] Pattern B → .data
        // Only for a reader the API would actually serve. Calling it regardless
        // would 403 on every non-administrator and put a red line in their
        // console on every page load.
        hasPermission('company:read') ? companiesApi.getAll({ limit: 1 }) : Promise.resolve(null), // [11] Pattern A, or null
      ]);

      const extract = <T,>(r: PromiseSettledResult<T>): T | null =>
        r.status === 'fulfilled' ? r.value : null;
      const extractAxios = <T,>(r: PromiseSettledResult<{ data: T }>): T | null =>
        r.status === 'fulfilled' ? r.value.data : null;

      setAnalytics(extract(results[0] as PromiseSettledResult<DashboardData>));
      setRecommendations(extract(results[1] as PromiseSettledResult<Recommendation[]>));
      setInventoryDash(extract(results[2] as PromiseSettledResult<InventoryDashboard>));
      setWorkOrderDash(
        extractAxios(results[3] as PromiseSettledResult<{ data: WorkOrderDashboard }>)
      );
      setEquipmentDash(
        extractAxios(results[4] as PromiseSettledResult<{ data: EquipmentDashboard }>)
      );
      setCertsDash(extract(results[5] as PromiseSettledResult<CertificatesDashboard>));
      setConsumptionDash(
        extractAxios(results[6] as PromiseSettledResult<{ data: ConsumptionDashboard }>)
      );
      setExceptions(extract(results[7] as PromiseSettledResult<Exceptions>));
      setTrend(extractAxios(results[8] as PromiseSettledResult<{ data: TrendPoint[] }>) ?? []);
      // jobsApi.getAll returns { data, meta }, not an array. The `as` cast on
      // this line said otherwise and the compiler believed it; the page threw
      // "jobs.filter is not a function" at runtime.
      setJobs(extract(results[9] as PromiseSettledResult<{ data: Job[] }>)?.data ?? []);
      setVarianceSummary(
        extractAxios(
          results[10] as PromiseSettledResult<{
            data: { summary: { averageVariancePercentage: number } };
          }>
        )?.summary ?? null
      );
      setCompanyCount(
        extract(results[11] as PromiseSettledResult<{ meta: { total: number } } | null>)?.meta
          ?.total ?? null
      );
      setLoading(false);
    }
    loadDashboard();
    // hasPermission is stable for the life of a session; the dashboard loads once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <DashboardSkeleton />;

  // Computed values
  const recentActivity = buildRecentActivity(analytics);

  // The KPI figures, each from a feed that already exists.
  const totalJobs = jobs.length;
  const activeJobs = jobs.filter((j) => j.status === 'PLANNED' || j.status === 'ACTIVE').length;
  const equipmentAvailable = equipmentDash?.byStatus?.AVAILABLE ?? 0;
  const overdueBack =
    (exceptions?.byKind?.ASSET_OVERDUE ?? 0) + (exceptions?.byKind?.RENTAL_OVERDUE ?? 0);
  const blockingCerts = exceptions?.byKind?.CERT_BLOCKING ?? 0;
  // Null rather than 0 where nothing was planned: no plan is not perfect
  // adherence, and painting it green would say the opposite of the truth.
  const variance = varianceSummary?.averageVariancePercentage ?? null;

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      {/* Page Header */}
      <motion.div variants={fadeUp}>
        <PageHeader
          title={`Welcome back, ${user?.fullName?.split(' ')[0] || 'User'}`}
          description="Current operating position across jobs, equipment, material consumption, and everything outstanding."
        />
      </motion.div>

      {/* ──── Row 1: Executive KPI Cards ──── */}
      <div className="mt-6">
        <KPIStrip columns={3}>
          {/* The operating position, not the balance sheet. Total inventory
              value led this strip and is the largest number on the page and the
              one least likely to change a decision before lunch; it now sits on
              the Inventory page where it belongs. */}
          <MetricSegment
            label="Jobs Active"
            value={activeJobs}
            sentiment="info"
            icon={<Briefcase className="h-5 w-5" />}
            subtitle={`${formatNumber(totalJobs)} on file`}
          />
          <MetricSegment
            label="Equipment Available"
            value={equipmentAvailable}
            sentiment={equipmentAvailable > 0 ? 'success' : 'warning'}
            icon={<Wrench className="h-5 w-5" />}
            subtitle={`${formatNumber(equipmentDash?.total ?? 0)} in the fleet`}
          />
          <MetricSegment
            label="Overdue Back"
            // Own assets still out plus hires past their return date. Both feeds
            // existed and neither reached this page before.
            value={overdueBack}
            sentiment={overdueBack > 0 ? 'danger' : 'success'}
            icon={<Undo2 className="h-5 w-5" />}
            subtitle={overdueBack > 0 ? 'assets and hires past their date' : 'nothing outstanding'}
          />
          <MetricSegment
            label="Blocking Certificates"
            value={blockingCerts}
            sentiment={blockingCerts > 0 ? 'danger' : 'success'}
            icon={<ShieldCheck className="h-5 w-5" />}
            subtitle={blockingCerts > 0 ? 'assets that cannot legally work' : 'none blocking'}
          />
          <MetricSegment
            label="Consumption vs Plan"
            value={
              variance == null ? 'Not measured' : `${variance > 0 ? '+' : ''}${variance.toFixed(1)}`
            }
            suffix={variance == null ? undefined : '%'}
            sentiment={
              variance == null
                ? 'neutral'
                : Math.abs(variance) <= 10
                  ? 'success'
                  : Math.abs(variance) <= 20
                    ? 'warning'
                    : 'danger'
            }
            icon={<TrendingUp className="h-5 w-5" />}
            subtitle={variance == null ? 'no plan recorded to compare' : 'against planned quantity'}
          />
          <MetricSegment
            label="Open Work Orders"
            value={workOrderDash?.total ?? 0}
            sentiment={(workOrderDash?.overdue ?? 0) > 0 ? 'warning' : 'success'}
            icon={<ClipboardCheck className="h-5 w-5" />}
            subtitle={workOrderDash?.overdue ? `${workOrderDash.overdue} overdue` : 'none overdue'}
          />
        </KPIStrip>
      </div>

      {/* ──── Band 1: what needs a decision, beside what just happened ────

          Two thirds to one, not tabs and not an even split. The pair are not
          peers: the worklist is the reason this page exists and the activity
          feed is ambient context, so the layout says which is which. Tabs were
          the other option and would have let a reader leave the dashboard
          parked on the feed with the exceptions hidden, which is the single
          thing band one exists to prevent.

          Both panels are capped at the same height and scroll inside it.
          Stacked and uncapped they ran past 1,300px before band two began. */}
      <motion.div
        variants={fadeUp}
        className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3 [--band-h:26rem]"
      >
        <div className="h-[var(--band-h)] lg:col-span-2">
          <ExceptionBand data={exceptions} loading={loading} />
        </div>

        <Card className="flex h-[var(--band-h)] flex-col border-border">
          <CardHeader className="pb-3">
            <h2 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-foreground">
              <BarChart3 className="h-5 w-5 text-primary-500" />
              Recent Activity
            </h2>
            <CardDescription className="text-muted-foreground">
              The latest movements and alerts recorded.
            </CardDescription>
          </CardHeader>
          <CardContent className="min-h-0 flex-1 overflow-y-auto">
            {recentActivity.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <Activity className="mb-2 h-10 w-10" />
                <p className="text-sm">No recent activity</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentActivity.map((item, idx) => (
                  <div
                    key={idx}
                    className="-mx-2 flex items-start gap-3 rounded-lg px-2 py-2 transition-colors duration-150 hover:bg-muted"
                  >
                    <div className={cn('mt-0.5 rounded-full p-2', item.typeStyle)}>{item.icon}</div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">{item.title}</p>
                      <p className="truncate text-xs text-muted-foreground">{item.description}</p>
                    </div>
                    <span className="whitespace-nowrap text-xs text-muted-foreground">
                      {item.time}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* ──── Band 2: how things stand ──── */}
      <motion.div variants={fadeUp} className="mt-6">
        <PositionBand
          loading={loading}
          data={{
            trend: trend,
            inventory: analytics?.distributions?.inventory ?? null,
            shipments: analytics?.distributions?.shipments ?? null,
            equipment: equipmentDash
              ? { byStatus: equipmentDash.byStatus, byCondition: equipmentDash.byCondition }
              : null,
            certificates: certsDash?.certificates?.buckets ?? null,
          }}
        />
      </motion.div>

      {/* ──── Band 3: what depends on who is reading ──── */}
      <motion.div variants={fadeUp} className="mt-6">
        <RoleBand
          hasPermission={hasPermission}
          pendingApprovals={analytics?.metrics?.pendingApprovals ?? 0}
          activeAlerts={analytics?.metrics?.activeAlerts ?? 0}
          companyCount={companyCount}
        />
      </motion.div>

      {/* ──── Row 4: AI Recommendations ──── */}
      {recommendations && recommendations.length > 0 && (
        <motion.div variants={fadeUp} className="mt-8">
          <Card className="border-border hover:shadow-raised-hover transition-all duration-200">
            <CardHeader>
              <h2 className="text-lg font-semibold tracking-tight text-foreground flex items-center gap-2">
                <Zap className="h-5 w-5 text-warning-500" />
                AI Recommendations
              </h2>
              <CardDescription className="text-muted-foreground">
                Intelligent suggestions to optimize your operations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {recommendations.slice(0, 4).map((rec) => (
                  <div
                    key={rec.id}
                    className="rounded-lg border border-border p-4 hover:border-border hover:shadow-raised-hover transition-all duration-150"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <Badge className={cn('text-xs', priorityStyles[rec.priority])}>
                        {rec.priority}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatSnakeCase(rec.type)}
                      </span>
                    </div>
                    <h4 className="text-sm font-semibold text-foreground mb-1">{rec.title}</h4>
                    <p className="text-xs text-muted-foreground line-clamp-2">{rec.description}</p>
                    {rec.suggestedAction && (
                      <div className="mt-2 text-xs text-primary-600 font-medium">
                        Suggested: {rec.suggestedAction.action}
                        {rec.suggestedAction.quantity != null &&
                          ` (${rec.suggestedAction.quantity} units)`}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </motion.main>
  );
}
